import { useEffect, useRef } from "react";
import * as maplibregl from "maplibre-gl";
import maplibreWorkerUrl from "maplibre-gl/dist/maplibre-gl-worker.mjs?url";
import MapboxDraw from "maplibre-gl-draw";
import "maplibre-gl/dist/maplibre-gl.css";
import "maplibre-gl-draw/dist/mapbox-gl-draw.css";
import type { CoverageAreaInput, CoveragePartyInput } from "@/lib/coverage/coverageLookup";
import { campaignFeatures, territoryFeatures } from "@/lib/coverage/coverageMapFeatures";
import type { CoverageCampaign } from "@/lib/coverage/coverageProblems";
import type { CoverageLocationPin } from "@/lib/coverage/coverageClient";

maplibregl.setWorkerUrl(maplibreWorkerUrl);

type Props = {
  parties: CoveragePartyInput[];
  areas: CoverageAreaInput[];
  campaigns: CoverageCampaign[];
  showCampaigns: boolean;
  locations: CoverageLocationPin[];
  customer: { lat: number; lng: number } | null;
  draftPolygon: unknown | null;
  drawing: boolean;
  onPolygon: (geojson: unknown) => void;
};

const EMPTY = { type: "FeatureCollection" as const, features: [] };

export function CoverageMap({
  parties,
  areas,
  campaigns,
  showCampaigns,
  locations,
  customer,
  draftPolygon,
  drawing,
  onPolygon,
}: Props) {
  const holder = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const onPolygonRef = useRef(onPolygon);
  onPolygonRef.current = onPolygon;

  useEffect(() => {
    const el = holder.current;
    if (!el || mapRef.current) return;
    const map = new maplibregl.Map({
      container: el,
      center: [-96, 38],
      zoom: 3.2,
      style: {
        version: 8,
        sources: {
          states: { type: "geojson", data: "/geo/us-states.geojson" },
          territories: { type: "geojson", data: EMPTY },
          campaigns: { type: "geojson", data: EMPTY },
          plants: { type: "geojson", data: EMPTY },
          sales: { type: "geojson", data: EMPTY },
          customer: { type: "geojson", data: EMPTY },
        },
        layers: [
          { id: "bg", type: "background", paint: { "background-color": "#e8eef4" } },
          { id: "states-fill", type: "fill", source: "states", paint: { "fill-color": "#f8fafc" } },
          {
            id: "states-line",
            type: "line",
            source: "states",
            paint: { "line-color": "#94a3b8", "line-width": 1 },
          },
          {
            id: "territories-fill",
            type: "fill",
            source: "territories",
            paint: {
              "fill-color": ["get", "color"],
              "fill-opacity": ["get", "fillOpacity"],
            },
          },
          {
            id: "territories-line",
            type: "line",
            source: "territories",
            paint: { "line-color": ["get", "color"], "line-width": 1.5 },
          },
          {
            id: "campaigns-fill",
            type: "fill",
            source: "campaigns",
            paint: { "fill-color": ["get", "color"], "fill-opacity": ["get", "fillOpacity"] },
          },
          {
            id: "campaigns-line",
            type: "line",
            source: "campaigns",
            paint: { "line-color": ["get", "color"], "line-width": 1.5 },
          },
          {
            id: "sales-dots",
            type: "circle",
            source: "sales",
            paint: { "circle-radius": 5, "circle-color": "#0f766e", "circle-stroke-width": 1, "circle-stroke-color": "#fff" },
          },
          {
            id: "plant-dots",
            type: "circle",
            source: "plants",
            paint: {
              "circle-radius": 7,
              "circle-color": ["get", "color"],
              "circle-stroke-width": 2,
              "circle-stroke-color": "#fff",
            },
          },
          {
            id: "customer-dot",
            type: "circle",
            source: "customer",
            paint: { "circle-radius": 8, "circle-color": "#111827", "circle-stroke-width": 2, "circle-stroke-color": "#fff" },
          },
        ],
      },
    });
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), "top-right");
    const resize = () => map.resize();
    map.on("load", resize);
    const observer = new ResizeObserver(resize);
    observer.observe(el);
    mapRef.current = map;
    return () => {
      observer.disconnect();
      map.remove();
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const apply = () => {
      const draftAreas = draftPolygon
        ? [
            ...areas,
            {
              partyId: "__draft__",
              kind: "polygon" as const,
              tier: "full" as const,
              radiusKm: null,
              centerLat: null,
              centerLng: null,
              geojson: draftPolygon,
            },
          ]
        : areas;
      const draftParties = draftPolygon
        ? [...parties, { id: "__draft__", name: "Draft", color: "#7c3aed", pricingUrl: "", program: "", lat: null, lng: null, active: true }]
        : parties;
      setGeo(map, "territories", territoryFeatures(draftParties, draftAreas));
      setGeo(map, "campaigns", campaignFeatures(campaigns));
      for (const id of ["campaigns-fill", "campaigns-line"]) {
        if (map.getLayer(id)) map.setLayoutProperty(id, "visibility", showCampaigns ? "visible" : "none");
      }
      setGeo(map, "plants", pointCollection(parties.filter((p) => p.active && p.lat != null && p.lng != null).map((p) => ({
        lng: p.lng as number,
        lat: p.lat as number,
        color: p.color,
        title: p.name,
      }))));
      setGeo(map, "sales", pointCollection(locations.map((pin) => ({ ...pin, color: "#0f766e", title: pin.name }))));
      setGeo(
        map,
        "customer",
        customer ? pointCollection([{ ...customer, color: "#111827", title: "Customer" }]) : EMPTY,
      );
    };
    if (map.isStyleLoaded()) apply();
    else map.once("load", apply);
  }, [parties, areas, campaigns, showCampaigns, locations, customer, draftPolygon]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !drawing) return;
    const draw = new MapboxDraw({ displayControlsDefault: false });
    map.addControl(draw as unknown as maplibregl.IControl);
    const start = () => draw.changeMode("draw_polygon");
    if (map.isStyleLoaded()) start();
    else map.once("load", start);
    const onCreate = () => {
      const feature = draw.getAll().features[0];
      if (feature?.geometry) onPolygonRef.current(feature.geometry);
      draw.deleteAll();
    };
    const events = map as maplibregl.Map & {
      on(type: string, listener: () => void): void;
      off(type: string, listener: () => void): void;
    };
    events.on("draw.create", onCreate);
    return () => {
      events.off("draw.create", onCreate);
      if (map.getStyle()) map.removeControl(draw as unknown as maplibregl.IControl);
    };
  }, [drawing]);

  return <div ref={holder} className="h-full min-h-[420px] w-full rounded-lg border border-gray-200 dark:border-zinc-700" />;
}

function setGeo(
  map: maplibregl.Map,
  id: string,
  data: GeoJSON.FeatureCollection | ReturnType<typeof territoryFeatures>,
) {
  const source = map.getSource(id) as maplibregl.GeoJSONSource | undefined;
  if (source && source.type === "geojson") source.setData(data);
}

function pointCollection(points: Array<{ lng: number; lat: number; color: string; title: string }>) {
  return {
    type: "FeatureCollection" as const,
    features: points.map((point) => ({
      type: "Feature" as const,
      properties: { color: point.color, title: point.title },
      geometry: { type: "Point" as const, coordinates: [point.lng, point.lat] },
    })),
  };
}
